<template>
  <div class="eudr-upload-panel">
    <div class="d-flex align-start">
      <v-file-input
        v-model="file"
        class="flex-grow-1"
        accept=".json,.geojson,.csv,.xlsx,.xls,.zip,application/json,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/zip,application/x-zip-compressed"
        :label="$t('chooseFile')"
        :hint="$t('eudrUploadFileHint')"
        persistent-hint
        dense
        show-size
        :disabled="uploading"
      />
      <Icon
        icon="mdi-information-outline"
        iconColor="info"
        margin="ml-2 mt-1"
        elementId="eudr-upload-format-info"
        tooltipClass="eudr-csv-format-tooltip"
        :tooltipText="$t('eudrCsvFormatInfo')"
      />
    </div>

    <!-- Read-only preview so the user can sanity-check coverage before submitting. -->
    <template v-if="previewHasFeatures">
      <div class="caption grey--text text--darken-1 mt-3">
        {{ $t('eudrUploadPreviewCount', { count: previewFeatureCollection.features.length }) }}
      </div>
      <div class="eudr-upload-preview mt-1">
        <EudrMap :key="previewVersion" :featureCollection="previewFeatureCollection" height="360px"/>
      </div>
      <v-btn
        small
        outlined
        color="primary"
        class="mt-2"
        id="eudr-edit-shapes"
        :disabled="parsing || uploading"
        @click="openShapeEditor"
      >
        <v-icon small left>mdi-vector-polyline-edit</v-icon>
        {{ $t('eudrEditShapes') }}
      </v-btn>
      <span v-if="sheetNotice" class="caption grey--text text--darken-1 d-block mt-2">{{ sheetNotice }}</span>
      <span v-if="layerNotice" class="caption grey--text text--darken-1 d-block mt-2">{{ layerNotice }}</span>
      <span v-if="dupeNotice" class="caption grey--text text--darken-1 d-block mt-2">{{ dupeNotice }}</span>
      <span v-if="skipped" class="caption warning--text d-block mt-2">{{ skipped }}</span>
      <v-alert v-if="pointNotice" type="info" dense text class="caption mt-2 mb-0">{{ pointNotice }}</v-alert>
      <v-alert v-if="invalidCoordinateCount" type="error" dense text class="caption mt-2 mb-0">
        {{ $t('eudrUploadInvalidCoordinates', { count: invalidCoordinateCount }) }}
      </v-alert>
      <v-alert v-if="geometryNotice" type="warning" dense text class="caption mt-2 mb-0">{{ geometryNotice }}</v-alert>
      <v-alert v-if="overlapNotice" type="warning" dense text class="caption mt-2 mb-0">{{ overlapNotice }}</v-alert>
    </template>

    <div class="d-flex mt-3">
      <v-spacer/>
      <v-btn
        color="primary"
        :loading="uploading"
        :disabled="uploading || parsing || !geojsonString || invalidCoordinateCount > 0"
        @click="submit"
      >
        <v-icon left>mdi-cloud-upload</v-icon>
        {{ $t('eudrSubmitUpload') }}
      </v-btn>
    </div>

    <Dialog :stateId="editorDialogId" @close="closeShapeEditor" @dialog-closing="closeShapeEditor">
      <EudrShapeEditor
        v-if="editingShapes"
        :featureCollection="previewFeatureCollection"
        @close="closeShapeEditor"
        @apply="onShapesEdited"
      />
    </Dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { buildEudrGeoJsonFromCsv } from '@/utils/eudr/EudrCsvToGeoJson.js'
import { analyzeFeatureCollection, GeometryWarning } from '@/utils/eudr/EudrGeometry.js'
import { buildEudrGeoJsonFromShapefile, removeExactDuplicateShapes } from '@/utils/eudr/EudrShapefile.js'
import { findOverlaps } from '@/utils/eudr/EudrOverlap.js'
import { uniqueDialogId } from '@/utils/componentHelpers'

export default {
  name: 'EudrUploadPanel',

  components: {
    EudrMap: () => import('@/components/eudr/EudrMap.vue'),
    EudrShapeEditor: () => import('@/components/eudr/EudrShapeEditor.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    accountId: { type: Number, required: true },
    // UTC ISO strings for the reporting window (computed by the parent from the selected month).
    startDate: { type: String, required: true },
    endDate: { type: String, required: true }
  },

  data () {
    return {
      file: null,
      uploading: false,
      parsing: false,
      previewFeatureCollection: null,
      previewVersion: 0,
      geojsonString: '',
      skipped: null,
      geometryNotice: null,
      overlapNotice: null,
      sheetNotice: null,
      layerNotice: null,
      dupeNotice: null,
      pointNotice: null,
      invalidCoordinateCount: 0,
      editingShapes: false,
      editorDialogId: uniqueDialogId('eudr-shape-editor-dialog')
    }
  },

  computed: {
    previewHasFeatures () {
      return this.previewFeatureCollection?.features?.length > 0
    }
  },

  watch: {
    file () {
      this.onFileChange()
    }
  },

  methods: {
    ...mapActions('eudr', ['uploadFile']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    openShapeEditor () {
      this.editingShapes = true
      this.openOrUpdateDialog({ id: this.editorDialogId, width: '90vw' })
    },

    closeShapeEditor () {
      this.editingShapes = false
      this.closeDialogsAtOrAbove(this.editorDialogId)
    },

    onShapesEdited (updated) {
      this.adoptPreview(updated)
      this.closeShapeEditor()
    },

    // Makes a parsed FeatureCollection the pending upload.
    adoptPreview (featureCollection) {
      // Provenance for downstream surfaces; the EU flavor strips properties, so it never reaches TRACES.
      const name = this.file?.name
      if (name) {
        for (const feature of featureCollection.features ?? []) {
          if (feature) feature.properties = { ...feature.properties, sourceFileName: name }
        }
      }
      this.previewFeatureCollection = Object.freeze(featureCollection)
      this.previewVersion++
      this.geojsonString = JSON.stringify(featureCollection)
      const { geometryNotice, overlapNotice, invalidCoordinateCount } = this.analyzePreview(featureCollection)
      this.geometryNotice = geometryNotice
      this.overlapNotice = overlapNotice
      this.invalidCoordinateCount = invalidCoordinateCount
    },

    isCsvFile (file) {
      return file?.name?.toLowerCase().endsWith('.csv') || file?.type === 'text/csv'
    },

    isExcelFile (file) {
      const name = file?.name?.toLowerCase() ?? ''
      return name.endsWith('.xlsx') || name.endsWith('.xls') ||
        file?.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file?.type === 'application/vnd.ms-excel'
    },

    // A zipped ESRI shapefile, same container the tract form's shape import accepts.
    isShapefileFile (file) {
      return file?.name?.toLowerCase().endsWith('.zip') ||
        file?.type === 'application/zip' ||
        file?.type === 'application/x-zip-compressed'
    },

    resetPreview () {
      this.previewFeatureCollection = null
      this.geojsonString = ''
      this.skipped = null
      this.geometryNotice = null
      this.overlapNotice = null
      this.sheetNotice = null
      this.layerNotice = null
      this.dupeNotice = null
      this.pointNotice = null
      this.invalidCoordinateCount = 0
    },

    // Informational, except invalid latitude/longitude, which blocks submit until corrected in Edit Shapes.
    analyzePreview (featureCollection) {
      const overlapNotice = this.buildOverlapNotice(featureCollection)
      const { byFeature, counts } = analyzeFeatureCollection(featureCollection)
      const invalidCodes = [GeometryWarning.CoordinateRange, GeometryWarning.MalformedCoordinates]
      const invalidCoordinateCount = [...byFeature.values()]
        .filter(warnings => warnings.some(code => invalidCodes.includes(code))).length
      const details = Object.entries(counts)
        .map(([code, count]) => `${this.$t(`eudrGeometry${code.charAt(0).toUpperCase()}${code.slice(1)}`)}: ${count}`)
        .join(', ')
      const geometryNotice = byFeature.size === 0
        ? null
        : this.$t('eudrUploadGeometryNotice', { count: byFeature.size, details })
      return { geometryNotice, overlapNotice, invalidCoordinateCount }
    },

    // Informational here; the same overlaps resurface as contested parcels at composite time.
    buildOverlapNotice (featureCollection) {
      const { overlappingCount } = findOverlaps(featureCollection)
      return overlappingCount > 0
        ? this.$t('eudrUploadOverlapNotice', { count: overlappingCount })
        : null
    },

    async onFileChange () {
      this.resetPreview()
      if (!this.file) return

      this.parsing = true
      try {
        if (this.isCsvFile(this.file)) {
          await this.parseCsv()
        } else if (this.isExcelFile(this.file)) {
          await this.parseExcel()
        } else if (this.isShapefileFile(this.file)) {
          await this.parseShapefile()
        } else {
          await this.parseGeoJson()
        }
      } catch (e) {
        // The parsers snack their own format errors; this catches outright read failures.
        this.setSnackError(this.$t('eudrFileReadFailed'))
      } finally {
        this.parsing = false
      }
    },

    async parseCsv () {
      await this.convertCsvText(await this.file.text())
    },

    // The first worksheet becomes CSV text and reuses the .csv path; SheetJS is imported lazily for weight.
    async parseExcel () {
      let csvText
      try {
        const xlsxModule = await import(/* webpackChunkName: "xlsx" */ 'xlsx')
        const XLSX = xlsxModule.default ?? xlsxModule
        const workbook = XLSX.read(new Uint8Array(await this.file.arrayBuffer()), { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        if (!sheetName) {
          this.setSnackError(this.$t('eudrCsvEmpty'))
          return
        }
        if (workbook.SheetNames.length > 1) {
          this.sheetNotice = this.$t('eudrXlsxFirstSheet', { name: sheetName })
        }
        csvText = XLSX.utils.sheet_to_csv(workbook.Sheets[sheetName])
      } catch (e) {
        this.setSnackError(this.$t('eudrXlsxUnreadable'))
        return
      }
      await this.convertCsvText(csvText)
    },

    // Entirely client-side, deliberately without a ReportAll lookup; coordinate-less tracts are not fatal.
    convertCsvText (csvText) {
      let result
      try {
        result = buildEudrGeoJsonFromCsv(csvText)
      } catch (e) {
        this.setSnackError(this.$t(e?.message === 'EUDR_CSV_HEADERS' ? 'eudrCsvBadHeaders' : 'eudrCsvEmpty'))
        return
      }

      if (result.pointCount === 0) {
        this.setSnackError(this.$t('eudrCsvNoPoints'))
        return
      }

      this.adoptPreview(result.geojson)
      this.pointNotice = result.duplicateCount > 0
        ? `${this.$t('eudrCsvPointsStored', { count: result.pointCount })} ${this.$t('eudrCsvDuplicates', { count: result.duplicateCount })}`
        : this.$t('eudrCsvPointsStored', { count: result.pointCount })
      this.skipped = result.unresolved.length > 0
        ? this.$t('eudrCsvUnresolved', { count: result.unresolved.length, total: result.tractCount })
        : null
    },

    async parseGeoJson () {
      let parsed
      try {
        parsed = JSON.parse(await this.file.text())
      } catch (e) {
        this.setSnackError(this.$t('eudrInvalidGeojson'))
        return
      }
      if (parsed?.type !== 'FeatureCollection' || !Array.isArray(parsed.features)) {
        this.setSnackError(this.$t('eudrInvalidGeojson'))
        return
      }
      // Third-party exports repeat boundaries; Angel exchange files are already clean, so they are skipped.
      if (parsed.angelEudrSchema == null) {
        const { features, removedCount } = removeExactDuplicateShapes(parsed.features)
        if (removedCount > 0) {
          parsed = { ...parsed, features }
          this.dupeNotice = this.$t('eudrDuplicateShapesRemoved', { count: removedCount })
        }
      }
      this.adoptPreview(parsed)
    },

    // EudrShapefile adds duplicate-layer detection: an ArcGIS geojson sidecar must be dropped, not merged.
    async parseShapefile () {
      let result
      try {
        result = await buildEudrGeoJsonFromShapefile(this.file)
      } catch (e) {
        this.setSnackError(this.$t(e?.message === 'EUDR_SHAPEFILE_EMPTY' ? 'eudrShapefileEmpty' : 'uploadedShapefileCannotDecode'))
        return
      }

      if (result.droppedLayerCount > 0) {
        this.layerNotice = this.$t('eudrShapefileDuplicateLayer', { count: result.droppedFeatureCount })
      }
      if (result.duplicateShapeCount > 0) {
        this.dupeNotice = this.$t('eudrDuplicateShapesRemoved', { count: result.duplicateShapeCount })
      }
      this.adoptPreview(result.geojson)
    },

    async submit () {
      if (!this.geojsonString || this.invalidCoordinateCount > 0) return
      this.uploading = true
      try {
        await this.uploadFile({
          accountId: this.accountId,
          startDate: this.startDate,
          endDate: this.endDate,
          geojson: this.geojsonString,
          sourceFileName: this.file?.name ?? null
        })
        // The interceptor's success snackbar fires first, so the partial-skip notice must come after it.
        if (this.skipped) this.setSnackError(this.skipped)
        this.file = null
        this.resetPreview()
        this.$emit('uploaded')
      } catch (e) {
        // Server validation (EAINT/EGJSN) surfaced by the error interceptor.
      } finally {
        this.uploading = false
      }
    }
  }
}
</script>

<style>
/* Unscopable (v-tooltip detaches its content); the cap stops one enormous line. */
.eudr-csv-format-tooltip {
  display: inline-block;
  max-width: 340px;
}
</style>
<style scoped>
.eudr-upload-preview {
  height: 360px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  overflow: hidden;
}
</style>
